import {
  clearGuestCredentials,
  getGuestCredentials,
} from 'state/auth-guest/hooks/use-guest-authentication';
import { IGuestCredentials } from 'state/auth-guest/types';

export class TokenRenewNowException extends Error {
  constructor() {
    super();
    this.name = 'TokenRenewException';
  }
}

export interface IGuestSessionData {
  AccessToken: string;
}

export class GuestToken {
  payload: string;
  decodedPayload: { [key: string]: any };

  constructor({ token }: { token: string }) {
    this.payload = token;
    this.decode();
  }

  public getJwtToken(): string {
    return this.payload;
  }
  public getExpiration(): number {
    return this.decodedPayload.exp;
  }
  public getIssuedAt(): number {
    return this.decodedPayload.iat;
  }
  public isExpired(): boolean {
    return Date.now() > this.getExpiration() * 1000;
  }
  public getName(): string | null {
    return this.decodedPayload.name || null;
  }
  public getEmail(): string | null {
    return this.decodedPayload.email || null;
  }
  public getSafestRenewalTimeout(): number {
    const oneSecondInMilliseconds = 1000;
    const forceRenewalTime = 3600 * oneSecondInMilliseconds;
    const expiration = this.getExpiration() * oneSecondInMilliseconds - forceRenewalTime;
    const safestExpirationTime = expiration - Date.now();
    if (safestExpirationTime <= forceRenewalTime) {
      throw new TokenRenewNowException(); // force renew before reach the safest expiration time
    }
    return safestExpirationTime;
  }

  private decode() {
    const payload = this.payload.split('.')[1];
    this.decodedPayload = JSON.parse(Buffer.from(payload, 'base64').toString('utf8'));
  }
}

export class GuestSession {
  token: GuestToken;

  constructor(guest_credentials: IGuestCredentials) {
    this.token = new GuestToken({ token: guest_credentials.generateGuestToken });
  }

  public getIdToken(): GuestToken {
    return this.token;
  }

  public isValid(): boolean {
    return !this.token.isExpired();
  }
}

// return the current guest user session
export const getCurrentGuestSession = (): GuestSession | null => {
  const guestTokenPayload = getGuestCredentials();
  if (!guestTokenPayload) {
    return null;
  }
  const guestSession = new GuestSession(guestTokenPayload);
  if (!guestSession.isValid()) {
    clearGuestCredentials();
    return null;
  }
  return guestSession;
};
